/**
 * Lifecycle utilities for soft-delete detection and constraints
 *
 * Per auth-contract.md:
 * - Deleted entities (is_deleted = true) are excluded from RBAC context
 * - Cannot modify deleted orgs/suborgs (returns 403)
 * - Cannot restore child suborg if parent org is deleted
 */

/**
 * Check if organization is soft-deleted
 */
export const isOrgDeleted = (org) => {
  if (!org || typeof org !== 'object') return false
  return org.is_deleted === true
}

/**
 * Check if sub-organization is soft-deleted
 */
export const isSubOrgDeleted = (subOrg) => {
  if (!subOrg || typeof subOrg !== 'object') return false
  return subOrg.is_deleted === true
}

/**
 * Check if a sub-org canbe restored (parent org must not be deleted)
 *
 * Per auth-contract.md:
 * "A sub-organization cannot be restored if its parent organization is still soft-deleted"
 */
export const canRestoreSubOrg = (subOrg, parentOrg) => {
  if (!subOrg || !parentOrg) return false
  if (!isSubOrgDeleted(subOrg)) return false  // Must be deleted to restore
  if (isOrgDeleted(parentOrg)) return false    // Can't restore if parent is deleted
  return true
}

/**
 * Check if an entity can be modified
 * Deleted entities cannot be modified (returns 403 on backend)
 */
export const canModifyOrg = (org) => {
  if (!org) return false
  return !isOrgDeleted(org)
}

export const canModifySubOrg = (subOrg) => {
  if (!subOrg) return false
  return !isSubOrgDeleted(subOrg)
}

/**
 * Filter deleted entities from a list (for  UI display)
 */
export const filterActiveOrgs = (orgs) => {
  if (!Array.isArray(orgs)) return []
  return orgs.filter(org => !isOrgDeleted(org))
}

export const filterActiveSubOrgs = (subOrgs) => {
  if (!Array.isArray(subOrgs)) return []
  return subOrgs.filter(sub => !isSubOrgDeleted(sub))
}

/**
 * Get all deleted orgs (for admin view)
 */
export const getDeletedOrgs = (orgs) => {
  if (!Array.isArray(orgs)) return []
  return orgs.filter(org => isOrgDeleted(org))
}

export const getDeletedSubOrgs = (subOrgs) => {
  if (!Array.isArray(subOrgs)) return []
  return subOrgs.filter(sub => isSubOrgDeleted(sub))
}

/**
 * Check if active org is deleted (lifecycle invalidation scenario)
 * If user's active org is deleted, they should be taken to a fallback
 */
export const isActiveOrgInvalidated = (activeOrgId, organizations) => {
  if (!activeOrgId || !Array.isArray(organizations)) return false
  const activeOrg = organizations.find(org => Number(org?.id) === Number(activeOrgId))
  if (!activeOrg) return false
  return isOrgDeleted(activeOrg)
}

export const isActiveSubOrgInvalidated = (activeSubOrgId, subOrgs) => {
  if (!activeSubOrgId || !Array.isArray(subOrgs)) return false
  const activeSubOrg = subOrgs.find(sub => Number(sub?.id) === Number(activeSubOrgId))
  return activeSubOrg && isSubOrgDeleted(activeSubOrg)
}

export const detectOrphanSubOrgs = (subOrgs, organizations) => {
  const orgMap = new Map((Array.isArray(organizations) ? organizations : []).map((org) => [Number(org?.id), org]))
  return (Array.isArray(subOrgs) ? subOrgs : []).filter((subOrg) => {
    const parentId = Number(subOrg?.organization_id)
    return Number.isInteger(parentId) && parentId > 0 && !orgMap.has(parentId)
  })
}

export const detectDeletedParentActiveChild = (subOrgs, organizations) => {
  const orgMap = new Map((Array.isArray(organizations) ? organizations : []).map((org) => [Number(org?.id), org]))
  return (Array.isArray(subOrgs) ? subOrgs : []).filter((subOrg) => {
    const parent = orgMap.get(Number(subOrg?.organization_id))
    if (!parent) return false
    return isOrgDeleted(parent) && !isSubOrgDeleted(subOrg)
  })
}

export const validateLifecycleHierarchy = ({ organizations = [], subOrgs = [] } = {}) => {
  const orphanSubOrgs = detectOrphanSubOrgs(subOrgs, organizations)
  const deletedParentActiveChildren = detectDeletedParentActiveChild(subOrgs, organizations)

  return {
    orphanSubOrgs,
    deletedParentActiveChildren,
    hasIssues: orphanSubOrgs.length > 0 || deletedParentActiveChildren.length > 0,
    warnings: [
      ...(orphanSubOrgs.length > 0 ? [`Orphaned sub-organizations detected: ${orphanSubOrgs.length}.`] : []),
      ...(deletedParentActiveChildren.length > 0 ? [`Active sub-organizations under deleted parent detected: ${deletedParentActiveChildren.length}.`] : []),
    ],
  }
}

export const detectRestoreConflicts = ({ organization, subOrgs = [], organizations = [] } = {}) => {
  const conflicts = []

  if (!organization) {
    conflicts.push('Organization not found.')
    return { hasConflict: true, conflicts }
  }

  if (!isOrgDeleted(organization)) {
    conflicts.push('Organization is already active.')
  }

  const subOrgList = Array.isArray(subOrgs) ? subOrgs : []
  const childCount = subOrgList.filter((item) => Number(item?.organization_id) === Number(organization?.id)).length
  const orphanCount = detectOrphanSubOrgs(subOrgList, organizations).length

  if (orphanCount > 0) {
    conflicts.push(`Restore blocked due to orphaned sub-organizations (${orphanCount}).`)
  }

  return {
    hasConflict: conflicts.length > 0,
    conflicts,
    childCount,
  }
}



